'use client';

import { useQueryClient } from '@tanstack/react-query';
import type { CartFragment, CartItemFragment } from '../cart/cart-fragment';
import { env, removeGid } from '../lib';
import { ELEVAR_CUSTOMER_QUERY_KEY } from './elevar-constants';
import type { ElevarAddToCartProduct, ElevarImpression } from './elevar-types';

export function useInvalidateElevarCustomerQuery() {
  const queryClient = useQueryClient();
  function invalidate() {
    queryClient.invalidateQueries({
      queryKey: [ELEVAR_CUSTOMER_QUERY_KEY],
    });
  }
  return invalidate;
}

export function serializeCartItemToElevarProduct({
  item,
  domain,
  list,
  position,
  quantity,
}: {
  item: CartItemFragment;
  domain: string;
  list: string;
  position?: number;
  quantity?: number; // Override for tracking actual add/remove amounts instead of total cart quantity
}): ElevarAddToCartProduct {
  return {
    id: item.merchandise.sku ?? '',
    name: item.merchandise.product.title,
    brand: item.merchandise.product.vendor ?? '',
    category: item.merchandise.product.category?.name ?? '',
    variant: item.merchandise.title,
    list,
    product_id: removeGid(item.merchandise.product.id),
    variant_id: removeGid(item.merchandise.id),
    price: item.cost.amountPerQuantity?.amount ?? '0',
    compare_at_price: item.cost.compareAtAmountPerQuantity?.amount ?? '0',
    image: item.merchandise.product.featuredImage?.url ?? '',
    url: `${domain}/products/${item.merchandise.product.handle}`,
    quantity: quantity ? quantity.toString() : item.quantity.toString(),
    ...(position ? { position } : {}),
  };
}

export function serializeCartToElevarImpressions({
  cart,
  domain,
  list = 'cart',
}: {
  cart: CartFragment;
  domain: string;
  list?: string;
}): ElevarImpression[] {
  return cart.lines.edges.map((line, index) => ({
    id: line.node.merchandise.sku ?? '',
    product_id: removeGid(line.node.merchandise.product.id),
    name: line.node.merchandise.product.title,
    price: line.node.cost.amountPerQuantity.amount,
    quantity: line.node.quantity.toString(),
    brand: line.node.merchandise.product.vendor ?? '',
    category: line.node.merchandise.product.category?.name ?? '',
    variant: line.node.merchandise.title,
    variant_id: removeGid(line.node.merchandise.id),
    compare_at_price: line.node.cost.compareAtAmountPerQuantity?.amount ?? '0',
    position: index + 1,
    image: line.node.merchandise.product.featuredImage?.url ?? '',
    url: `${domain}/products/${line.node.merchandise.product.handle}`,
    list,
  }));
}

export function checkIfSiteHasElevar() {
  const site = env.NEXT_PUBLIC_SITE_NAME;

  if (site === 'trade') {
    return false;
  }

  return true;
}
